import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { states, StateInfo } from "@/data/stateData";
import { statesFaq } from "@/data/landingFaqs";

const PATH = "/states";

const formatPct = (value: number) => `${Math.round(value * 100)}%`;
const formatShare = (value: number) => `${(value * 100).toFixed(1)}%`;
const formatMillions = (value: number) =>
  value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(2)} million`
    : value.toLocaleString("en-US");

function extremes<K extends keyof StateInfo>(key: K) {
  const sorted = [...states].sort((a, b) => Number(a[key]) - Number(b[key]));
  return { low: sorted[0], high: sorted[sorted.length - 1] };
}

function nationalShare(key: "pctBachelorsWomen" | "pctNeverMarriedWomen") {
  const weighted = states.reduce((sum, s) => sum + s[key] * s.women18to44, 0);
  const total = states.reduce((sum, s) => sum + s.women18to44, 0);
  return weighted / total;
}

const StatesHub = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Dating Pool Statistics by State",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: meta?.structuredData,
  });

  const pool = extremes("women18to44");
  const age = extremes("medianAge");
  const bachelors = extremes("pctBachelorsWomen");
  const neverMarried = extremes("pctNeverMarriedWomen");
  const totalWomen = states.reduce((sum, s) => sum + s.women18to44, 0);
  const bySize = [...states].sort((a, b) => b.women18to44 - a.women18to44);
  const topFiveShare = bySize.slice(0, 5).reduce((sum, s) => sum + s.shareOfUsWomen, 0);

  const takeaways = [
    {
      title: "Biggest and smallest pools",
      body: `${pool.high.name} has an estimated ${formatMillions(pool.high.women18to44)} women aged 18-44, about ${formatShare(pool.high.shareOfUsWomen)} of the national total. ${pool.low.name} has about ${pool.low.women18to44.toLocaleString("en-US")}. The five largest states (${bySize
        .slice(0, 5)
        .map((s) => s.name)
        .join(", ")}) together hold ${formatShare(topFiveShare)} of the pool.`,
    },
    {
      title: "Youngest and oldest states",
      body: `${age.low.name} has the lowest median age at ${age.low.medianAge.toFixed(1)}, while ${age.high.name} is the oldest at ${age.high.medianAge.toFixed(1)}. Older states have proportionally fewer women in the 18-44 range for the same total population.`,
    },
    {
      title: "Education",
      body: `${formatPct(bachelors.high.pctBachelorsWomen)} of women 25 and older in ${bachelors.high.name} hold a bachelor's degree or higher, against ${formatPct(bachelors.low.pctBachelorsWomen)} in ${bachelors.low.name} (population-weighted average across states: ${formatPct(nationalShare("pctBachelorsWomen"))}). An education filter therefore removes far fewer people in the top states than the national math suggests.`,
    },
    {
      title: "Never married",
      body: `${formatPct(neverMarried.high.pctNeverMarriedWomen)} of adult women in ${neverMarried.high.name} have never married, versus ${formatPct(neverMarried.low.pctNeverMarriedWomen)} in ${neverMarried.low.name} (weighted average: ${formatPct(nationalShare("pctNeverMarriedWomen"))}). Marriage timing, not just age, drives the gap.`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-10">
          <header className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Dating Pool Statistics by State
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              National probabilities are only half the story. These estimates show how
              many of the {formatMillions(totalWomen)} US women aged 18-44 live in each
              state, plus the education and marriage statistics that shape the local
              dating pool.
            </p>
          </header>

          <main className="space-y-10">
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">Key takeaways</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {takeaways.map((t) => (
                  <div key={t.title} className="rounded-lg border p-5 space-y-2">
                    <h3 className="font-semibold">{t.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">All 50 states and DC</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableCaption>
                    Estimates derived from US Census Bureau 2023 population data and
                    American Community Survey statistics.
                  </TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Women 18-44 (est.)</TableHead>
                      <TableHead className="text-right">Share of US pool</TableHead>
                      <TableHead className="text-right">Median age</TableHead>
                      <TableHead className="text-right">Bachelor's+ (women)</TableHead>
                      <TableHead className="text-right">Never married (women)</TableHead>
                      <TableHead>Largest metro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {states.map((state) => (
                      <TableRow key={state.slug}>
                        <TableCell>
                          <Link
                            to={`/states/${state.slug}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {state.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right">
                          {state.women18to44.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatShare(state.shareOfUsWomen)}
                        </TableCell>
                        <TableCell className="text-right">{state.medianAge.toFixed(1)}</TableCell>
                        <TableCell className="text-right">{formatPct(state.pctBachelorsWomen)}</TableCell>
                        <TableCell className="text-right">{formatPct(state.pctNeverMarriedWomen)}</TableCell>
                        <TableCell>{state.largestMetro}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl font-semibold">How to use these numbers</h2>
              <p className="text-muted-foreground leading-relaxed">
                The calculators return a national probability: the share of US adults who
                match every filter you choose. Multiply that share by a state's estimated
                women aged 18-44 to get a rough local count. Preferences matching 1% of US
                women work out to about{" "}
                {Math.round(pool.high.women18to44 * 0.01).toLocaleString("en-US")} women in{" "}
                {pool.high.name} and about{" "}
                {Math.round(pool.low.women18to44 * 0.01).toLocaleString("en-US")} in{" "}
                {pool.low.name}. Adjust up or down for filters where a state differs from
                the national average, such as education or marital status.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Estimates combine 2023 Census Bureau state population figures with
                American Community Survey age, sex, education, and marital status
                distributions, rounded to the nearest thousand. They are for statistical
                comparison, not exact headcounts. See our{" "}
                <Link to="/methodology" className="text-primary hover:underline">
                  methodology
                </Link>{" "}
                and{" "}
                <Link to="/data-sources" className="text-primary hover:underline">
                  data sources
                </Link>
                .
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">Frequently asked questions</h2>
              {/* Default-open so answers are present in prerendered HTML */}
              <Accordion
                type="multiple"
                defaultValue={statesFaq.map((_, i) => `faq-${i}`)}
                className="w-full"
              >
                {statesFaq.map((item, i) => (
                  <AccordionItem key={item.question} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </main>

          <section className="bg-muted/50 rounded-lg p-6 text-center space-y-3">
            <h2 className="text-2xl font-semibold">See your personal probability</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Pick a state for a detailed breakdown, or go straight to the calculator to
              combine filters like age, education, and body type.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <Link to="/" className="text-primary hover:underline font-medium">
                Female Partner Calculator →
              </Link>
              <Link to="/ideal-woman-calculator" className="text-primary hover:underline font-medium">
                Ideal Woman Calculator →
              </Link>
              <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                Male Partner Calculator →
              </Link>
              <Link to="/dating-standards-calculator" className="text-primary hover:underline font-medium">
                Dating Standards Test →
              </Link>
              <Link to="/stats" className="text-primary hover:underline font-medium">
                US Dating Statistics →
              </Link>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StatesHub;
